// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    canCheckoutWithCart,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@pace/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@pace/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@pace/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@pace/App/Components/Notify/Notify";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { xIsAuthenticated } from "@pace/App/Store/Selectors/GeneralSelectors";
import { fontConfig } from "@pace/XDL/PreStyle/typography";
import { color, styledScrollbar, xScreen } from "@pace/XDL/Utilities";
import { mergeWithCss } from "@pace/XDL/Utilities/mergeWithCss";
import React, { FC, useEffect, useState } from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isPopupShown?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const { isUpdatingCart } = state.context;

    return {
        cart,
        isCartLoading: cartState.isLoading,
        isCartCheckoutDisabled: cartState.isLoading || !cart || isCartCheckoutDisabled(cart) || isUpdatingCart,
        canSubmitRequisition: canSubmitRequisition(cart),
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
        isAuthenticated: xIsAuthenticated(state),
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        isSpecialOrderRequested: cart?.properties["isSpecialOrderRequestedForOutOfStockItems"],
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    wrapper?: InjectableCss;
    centerWrapper?: InjectableCss;
    notifyWrapper?: InjectableCss;
    noCartLinesNotify?: TypographyProps;
    totalWrapper?: InjectableCss;
    title?: TypographyProps;
    totalItems?: TypographyProps;
    viewCartLink?: LinkPresentationProps;
    cartLinesContainer?: InjectableCss;
    cartLinesWrapper?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    bottomWrapper?: InjectableCss;
    bottomSectionWrapper?: InjectableCss;
    totalItem?: InjectableCss;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    checkoutButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
}

const miniCartStyles: MiniCartStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            max-width: 466px;
            min-width: 250px;
        `,
    },
    centerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            max-width: 466px;
            min-width: 250px;
        `,
    },
    notifyWrapper: {
        css: css`
            padding: 16px;
        `,
    },
    noCartLinesNotify: {
        css: css`
            margin-bottom: 0;
        `,
    },
    viewCartLink: {
        color: "secondary",
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
    cartLinesContainer: {
        css: css`
            padding: 16px 5px 0 16px;
        `,
    },
    cartLinesWrapper: {
        css: css`
            position: relative;
            min-height: 100px;
            max-height: 560px;
            padding-right: 12px;
            overflow-x: hidden;
            ${styledScrollbar(8, 8)}

            @media screen and (max-height: 850px) {
                max-height: 435px;
            }
        `,
    },
    productAvailabilityStyles: {
        container: {
            gap: 0,
            css: css`
                width: auto;
                flex: 1 0 auto;
            `,
        },
    },
    bottomWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            border-top: 1px solid ${color("common.border")};
            padding: 16px;
            row-gap: 12px;
        `,
    },
    bottomSectionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            position: relative;
            column-gap: 12px;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
            }
        `,
    },
    totalItem: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    totalLabel: {
        size: 14,
        weight: fontConfig.weight.regular,
        lineHeight: "22px",
        css: css`
            margin-right: 8px;
        `,
    },
    totalValue: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
        `,
    },
    checkoutButton: {
        mergeCss: true,
        css: css`
            width: 100%;
            ${xScreen("lte", "xs")} {
                margin-bottom: 12px;
            }
        `,
    },
    viewCartButton: {
        variant: "secondary",
        mergeCss: true,
        css: css`
            width: 100%;
            ${xScreen("lte", "xs")} {
                margin-bottom: 12px;
            }
        `,
    },
};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    isCartLoading,
    cartPageUrl,
    requisitionConfirmationPageUrl,
    isPunchOutOrder,
    isCartCheckoutDisabled,
    totalCountDisplay,
    orderSubTotalDisplay,
    removeCartLine,
    updateCartLine,
    submitRequisition,
    preloadCheckoutShippingData,
    canSubmitRequisition,
    doPunchOutCheckout,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isUpdatingCartLine,
    isRemovingCartLine,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    isPopupShown,
    canCheckoutWithCart,
    isAuthenticated,
    isSpecialOrderRequested,
}) => {
    const history = useHistory();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);
        setIsCheckoutLoading(true);

        const { canBypassCheckoutAddress } = cart!;
        if (canBypassCheckoutAddress) {
            setIsCheckoutLoading(false);

            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: cart?.isAwaitingApproval ? cart.id : undefined,
            onSuccess: () => {
                setIsCheckoutLoading(false);
                history.push(
                    cart?.isAwaitingApproval
                        ? `${checkoutShippingPageUrl}?cartId=${cart.id}`
                        : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        hideTippy();
        history.push(cartPageUrl);
    };

    if (!cart && isCartLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!cart) {
        return null;
    }

    const cartLines = cart.cartLines ?? [];

    if (!cartLines?.length) {
        return (
            <StyledWrapper {...mergeWithCss(styles.wrapper, styles.notifyWrapper)}>
                <Notify {...styles.noCartLinesNotify} type="info" showClose={false}>
                    {isAuthenticated && siteMessage("Custom_Cart_Empty_Message")}
                </Notify>
            </StyledWrapper>
        );
    }

    return (
        <>
            <LoadingOverlay
                {...styles.loadingOverlay}
                loading={isCartLoading || isUpdatingCartLine || isRemovingCartLine}
            >
                <StyledWrapper {...styles.cartLinesContainer}>
                    <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef}>
                        <StyledWrapper
                            {...styles.cartLinesWrapper}
                            ref={parentBoxRef}
                            data-test-selector="cartLines-container"
                        >
                            {isSpecialOrderRequested && (
                                <Notify type="warning" sizeVariant="small" showClose={false} fullWidth={true}>
                                    {siteMessage("Custom_Cart_SpecialOrderRequested")}
                                </Notify>
                            )}
                            {cartLines &&
                                cartLines?.map(cartLine => {
                                    const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                                    return (
                                        <MiniCartLineCard
                                            key={`miniCartLine_${cartLine.id}`}
                                            cartLine={cartLine}
                                            cart={cart}
                                            hideTippy={hideTippy}
                                            qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                            removeCartLineClickHandler={removeCartLineClickHandler}
                                            errorMessages={errorMessages}
                                        />
                                    );
                                })}
                        </StyledWrapper>
                    </FancyScroll>
                </StyledWrapper>
                <StyledWrapper {...styles.bottomWrapper}>
                    <StyledWrapper {...styles.bottomSectionWrapper}>
                        <StyledWrapper {...styles.totalItem}>
                            <Typography {...styles.totalLabel}>{`${translate("Items")}:`}</Typography>
                            <Typography {...styles.totalValue}>{totalCountDisplay}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.totalItem}>
                            <Typography {...styles.totalLabel}>{`${translate("Subtotal")}:`}</Typography>
                            <Typography {...styles.totalValue}>{orderSubTotalDisplay}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.bottomSectionWrapper}>
                        <Button {...styles.viewCartButton} onClick={handleViewCarClick}>
                            {translate("View Cart")}
                        </Button>
                        {canCheckoutWithCart && (
                            <>
                                {!canSubmitRequisition ? (
                                    <Button
                                        {...styles.checkoutButton}
                                        onClick={checkoutHandler}
                                        disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
                                    >
                                        {cart?.requiresApproval
                                            ? translate("Checkout for Approval")
                                            : isPunchOutOrder
                                            ? translate("Return to Procurement System")
                                            : translate("Checkout")}
                                    </Button>
                                ) : (
                                    <Button
                                        {...styles.checkoutButton}
                                        onClick={submitRequisitionClickHandler}
                                        data-test-selector="cartTotal_SubmitRequisition"
                                    >
                                        {translate("Submit Requisition")}
                                    </Button>
                                )}
                            </>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </LoadingOverlay>
            <TwoButtonModal
                headlineText=""
                modalIsOpen={quoteRequiredModalIsOpen}
                messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Continue")}
                onCancel={() => setQuoteRequiredModalIsOpen(false)}
                onSubmit={checkoutHandler}
                submitTestSelector="QuoteInCartMessageSubmit"
                dataTestSelector="twoButtonModal_RequiresQuote"
            ></TwoButtonModal>
        </>
    );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
